import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FiMenu, FiX } from 'react-icons/fi';
import './Home.css';
import { formatPhone } from '../utils/formatCpfCnpj';

const CONTACTS = {
  whatsappNumberE164: '5567999999999',
  emailTo: 'contato@greencheck.com.br'
};

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

export default function Home() {
  const [navOpen, setNavOpen] = useState(false);
  const [demoModalOpen, setDemoModalOpen] = useState(false);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [faqOpen, setFaqOpen] = useState(null);
  const [copyFeedback, setCopyFeedback] = useState(null);
  const trialFormRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('show');
        });
      },
      { threshold: 0.08 }
    );
    document.querySelectorAll('.reveal').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const handleTrialSubmit = (e) => {
    e.preventDefault();
    const form = e.target;
    if (!form.checkValidity()) {
      form.classList.add('was-validated');
      return;
    }
    const nome = form.nome.value.trim();
    const empresa = form.empresa.value.trim();
    const whatsapp = form.whatsapp.value.trim();
    const text = `Olá! Quero ativar o teste de 7 dias do Green Check.\n\nNome: ${nome}\nEmpresa: ${empresa}\nWhatsApp: ${whatsapp}\n\nPosso enviar o PDF da licença por aqui?`;
    window.open(`https://wa.me/${CONTACTS.whatsappNumberE164}?text=${encodeURIComponent(text)}`, '_blank', 'noopener');
  };

  const copyTrialMessage = () => {
    const form = trialFormRef.current;
    if (!form || !form.checkValidity()) {
      form?.classList.add('was-validated');
      return;
    }
    const nome = form.nome.value.trim();
    const empresa = form.empresa.value.trim();
    const whatsapp = form.whatsapp.value.trim();
    const text = `Olá! Quero ativar o teste de 7 dias do Green Check.\n\nNome: ${nome}\nEmpresa: ${empresa}\nWhatsApp: ${whatsapp}\n\nPosso enviar o PDF da licença por aqui?`;
    navigator.clipboard.writeText(text).then(
      () => {
        setCopyFeedback('trial');
        setTimeout(() => setCopyFeedback(null), 1400);
      },
      () => alert('Não foi possível copiar. Copie manualmente.')
    );
  };

  const openWhatsContact = () => {
    const text = `Olá! Quero falar sobre o Green Check (plano Corporate / multiunidade). Pode me passar uma proposta?`;
    window.open(`https://wa.me/${CONTACTS.whatsappNumberE164}?text=${encodeURIComponent(text)}`, '_blank', 'noopener');
  };

  const copyEmailContact = () => {
    const text = `Assunto: Proposta Green Check (Corporate)\n\nOlá,\nGostaria de receber uma proposta do Green Check para operação multiunidade (plano Corporate).\nPodemos agendar uma call de 30 minutos?\n\nObrigado.`;
    navigator.clipboard.writeText(text).then(
      () => {
        setCopyFeedback('email');
        setTimeout(() => setCopyFeedback(null), 1400);
      },
      () => alert('Não foi possível copiar.')
    );
  };

  const faqItems = [
    { id: 1, q: 'O que conta como "licença"?', a: 'Qualquer documento com condicionantes e prazos: LP/LI/LO, Outorga, Autorizações e licenças municipais/estaduais aplicáveis.' },
    { id: 2, q: 'O teste é freemium?', a: 'Não. O teste é de 7 dias, controlado, para você ver valor real (condicionantes → tarefas → evidências). Sem cartão.' },
    { id: 3, q: 'Precisa de implantação (setup)?', a: 'Sim — é o que garante que suas licenças virem tarefas com evidências e relatórios corretamente. No teste, fazemos uma ativação simplificada com 1 licença.' },
    { id: 4, q: 'Dá para usar com consultorias ambientais?', a: 'Sim. O Corporate pode incluir painel multi-clientes, governança e relatórios por cliente. Fale com o comercial para proposta.' }
  ];

  return (
    <div className="home-landing">
      <nav className="home-nav">
        <div className="home-nav-inner">
          <Link to="/" className="home-brand" onClick={() => setNavOpen(false)}>
            <span className="home-brand-green">Green</span> Check
          </Link>
          <button type="button" className="home-nav-toggle" onClick={() => setNavOpen(!navOpen)} aria-label="Menu">
            {navOpen ? <FiX /> : <FiMenu />}
          </button>
          <div className={`home-nav-links ${navOpen ? 'open' : ''}`}>
            <button type="button" className="home-nav-link" onClick={() => { scrollToId('como-funciona'); setNavOpen(false); }}>Como funciona</button>
            <button type="button" className="home-nav-link" onClick={() => { scrollToId('beneficios'); setNavOpen(false); }}>Benefícios</button>
            <button type="button" className="home-nav-link" onClick={() => { scrollToId('planos'); setNavOpen(false); }}>Planos</button>
            <button type="button" className="home-nav-link" onClick={() => { scrollToId('faq'); setNavOpen(false); }}>FAQ</button>
            <button type="button" className="home-nav-cta btn btn-primary" onClick={() => { scrollToId('trial'); setNavOpen(false); }}>Testar 7 dias</button>
            <Link to="/login" className="home-nav-login btn btn-outline" onClick={() => setNavOpen(false)}>Entrar</Link>
          </div>
        </div>
      </nav>

      <header id="top" className="home-hero">
        <div className="home-container">
          <div className="home-hero-grid">
            <div className="home-hero-text reveal">
              <span className="home-badge">Gestão de Licenças & Condicionantes • IA + Trilha Auditável</span>
              <h1 className="home-hero-title">
                Transforme sua licença ambiental em <span className="text-accent">tarefas</span>,{' '}
                <span className="text-success">evidências</span> e <span className="text-accent-2">relatórios</span>.
              </h1>
              <p className="home-hero-lead">
                O Green Check organiza documentos, extrai condicionantes automaticamente com IA e acompanha prazos
                para reduzir risco de multas, autuações e passivos ambientais.
              </p>
              <div className="home-hero-buttons">
                <button type="button" className="btn btn-primary" onClick={() => scrollToId('trial')}>Testar 7 dias</button>
                <button type="button" className="btn btn-outline" onClick={() => setDemoModalOpen(true)}>Ver demo (2 min)</button>
              </div>
              <div className="home-kpis">
                <div className="home-kpi"><div className="home-kpi-title">PDF → Condicionantes</div><div className="home-kpi-desc">Extração automática com IA</div></div>
                <div className="home-kpi"><div className="home-kpi-title">Plano de ação</div><div className="home-kpi-desc">Tarefas + recorrências</div></div>
                <div className="home-kpi"><div className="home-kpi-title">Auditoria</div><div className="home-kpi-desc">Evidências e trilha</div></div>
              </div>
            </div>
            <div className="home-hero-card-wrap reveal">
              <div className="home-card home-hero-card">
                <div className="home-card-header">
                  <div><div className="fw-bold">Resultado esperado (7 dias)</div><div className="home-small-note">Onboarding guiado</div></div>
                  <span className="home-badge home-badge-success">Trial</span>
                </div>
                <div className="home-divider" />
                <ul className="home-list-unstyled">
                  <li><span className="home-feature-icon">✅</span><div><strong>Documentos organizados</strong><div className="muted">Repositório padronizado</div></div></li>
                  <li><span className="home-feature-icon">🤖</span><div><strong>Condicionantes estruturadas</strong><div className="muted">IA extrai prazos e exigências</div></div></li>
                  <li><span className="home-feature-icon">🧾</span><div><strong>Evidências anexadas</strong><div className="muted">Sem evidência, não conclui</div></div></li>
                  <li><span className="home-feature-icon">📊</span><div><strong>Relatório executivo</strong><div className="muted">Status, riscos e próximos prazos</div></div></li>
                </ul>
                <div className="home-divider" />
                <p className="home-small-note"><strong>Sem cartão.</strong> Ativamos o teste após receber 1 PDF de licença.</p>
                <button type="button" className="btn btn-primary w-100" onClick={() => scrollToId('trial')}>Quero testar agora</button>
              </div>
            </div>
          </div>
        </div>
      </header>

      <section id="como-funciona" className="home-section">
        <div className="home-container">
          <div className="home-section-header reveal">
            <h2 className="home-section-title">Como funciona</h2>
            <p className="home-section-lead">Processo simplificado em 3 etapas para transformar licenças ambientais em gestão estruturada e rastreável.</p>
          </div>
          <div className="home-steps">
            <div className="home-card reveal">
              <div className="home-step-num">1</div>
              <h3 className="home-step-title">Importe a licença</h3>
              <p className="muted">Faça upload do PDF da licença ambiental e documentos relacionados. O sistema organiza automaticamente em repositório padronizado.</p>
            </div>
            <div className="home-card reveal">
              <div className="home-step-num">2</div>
              <h3 className="home-step-title">IA extrai condicionantes</h3>
              <p className="muted">Inteligência artificial identifica automaticamente prazos, periodicidade, exigências e evidências esperadas de cada condicionante.</p>
            </div>
            <div className="home-card reveal">
              <div className="home-step-num">3</div>
              <h3 className="home-step-title">Execute e audite</h3>
              <p className="muted">Acompanhe tarefas recorrentes, anexe evidências obrigatórias e gere relatórios executivos com trilha auditável completa.</p>
            </div>
          </div>
          <div className="home-process-box reveal">
            <div className="home-card">
              <div className="home-process-box-inner">
                <div>
                  <div className="fw-bold">O “processo completo” do Green Check</div>
                  <p className="muted mb-0">PDF da licença → condicionantes estruturadas → tarefas + gestão de prazos e equipes + evidências com rastreabilidade → relatórios detalhados</p>
                </div>
                <button type="button" className="btn btn-outline" onClick={() => scrollToId('trial')}>Ver teste de 7 dias</button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="prova-social" className="home-section">
        <div className="home-container">
          <div className="home-badge-wrap reveal">
            <span className="home-badge">Já usado por operações em MS</span>
          </div>
          <div className="home-prova-grid">
            <div className="home-card reveal">
              <div className="home-depoimento">
                <div className="home-feature-icon">💬</div>
                <div>
                  <div className="fw-bold mb-2">Depoimento</div>
                  <p className="fst-italic text-secondary">"O Green Check transformou nossa gestão de licenças. Antes perdíamos prazos e documentos. Agora tudo está organizado, com alertas automáticos e evidências prontas para auditoria."</p>
                  <div className="home-small-note"><strong>Cliente Piloto</strong> • Indústria em MS</div>
                </div>
              </div>
            </div>
            <div className="home-card reveal">
              <div className="fw-bold mb-3">Case rápido</div>
              <ul className="home-list-unstyled mb-0">
                <li className="home-case-item"><span className="home-badge home-badge-danger">Problema</span><span className="text-secondary">Licenças desorganizadas, prazos perdidos e risco de multas ambientais.</span></li>
                <li className="home-case-item"><span className="home-badge home-badge-warning">Tempo</span><span className="text-secondary">Setup em 3 dias. Primeira licença estruturada em 1 semana.</span></li>
                <li className="home-case-item"><span className="home-badge home-badge-success">Resultado</span><span className="text-secondary">100% das condicionantes rastreadas. Zero prazos perdidos. Evidências prontas para auditoria.</span></li>
                <li className="home-case-item"><span className="home-badge home-badge-info">Benefício</span><span className="text-secondary">Redução de 80% no tempo de gestão. Tranquilidade em fiscalizações e renovações.</span></li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section id="beneficios" className="home-section">
        <div className="home-container">
          <div className="home-section-header reveal">
            <h2 className="home-section-title">Benefícios</h2>
            <p className="home-section-lead">Menos risco, mais controle e evidência pronta quando alguém pedir (fiscalização, auditoria, banco, investidor).</p>
          </div>
          <div className="home-beneficios-grid">
            {[
              { icon: '⏰', title: 'Alertas de vencimento', desc: 'Evite atraso de prazos e renovações.' },
              { icon: '📌', title: 'Plano de ação claro', desc: 'Tarefas com responsáveis e recorrência.' },
              { icon: '🧾', title: 'Evidências auditáveis', desc: 'Histórico completo: quem fez, quando, e anexos.' },
              { icon: '🤖', title: 'IA para condicionantes', desc: 'Menos trabalho manual e menos erro humano.' },
              { icon: '📊', title: 'Relatório executivo', desc: 'Status, riscos e próximos prazos em 1 página.' },
              { icon: '🔒', title: 'Governança', desc: 'Controle de acesso por usuário e trilha de mudanças.' }
            ].map((b, i) => (
              <div key={i} className="home-card reveal">
                <div className="home-beneficio">
                  <div className="home-feature-icon">{b.icon}</div>
                  <div><div className="fw-bold">{b.title}</div><div className="muted">{b.desc}</div></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="planos" className="home-section">
        <div className="home-container">
          <div className="home-section-header reveal">
            <h2 className="home-section-title">Planos</h2>
            <p className="home-section-lead">Comece com o teste de 7 dias. Depois escolha o plano ideal para sua operação.</p>
          </div>
          <div className="home-planos-grid">
            <div className="home-card home-pricing-card reveal">
              <div className="home-pricing-header">
                <h3 className="h5 fw-bold mb-0">Start</h3>
                <span className="home-badge">Pequeno / Rural</span>
              </div>
              <div className="home-price">R$ 297<span className="home-price-unit">/mês</span></div>
              <p className="muted small">Setup único: <strong>R$ 590</strong></p>
              <ul className="home-list-unstyled home-pricing-list">
                <li>1 empreendimento</li><li>Até 3 licenças</li><li>Até 5 usuários</li><li>Alertas e tarefas básicas</li><li>Evidências (básico)</li>
              </ul>
              <button type="button" className="btn btn-outline w-100" onClick={() => scrollToId('trial')}>Testar 7 dias</button>
            </div>
            <div className="home-card home-pricing-card home-pricing-pro reveal">
              <div className="home-pricing-header">
                <h3 className="h5 fw-bold mb-0">Pro</h3>
                <span className="home-badge home-badge-info">Mais vendido</span>
              </div>
              <div className="home-price">R$ 897<span className="home-price-unit">/mês</span></div>
              <p className="muted small">Setup único: <strong>R$ 1.990</strong></p>
              <ul className="home-list-unstyled home-pricing-list">
                <li>Até 5 empreendimentos</li><li>Até 5 licenças por empreendimento (máx. 25)</li><li>Até 20 usuários</li><li><strong>IA para extração automática de condicionantes</strong></li><li>Tarefas recorrentes + evidência obrigatória</li><li>Trilha auditável + relatório executivo</li>
              </ul>
              <button type="button" className="btn btn-primary w-100" onClick={() => scrollToId('trial')}>Testar 7 dias</button>
            </div>
            <div className="home-card home-pricing-card reveal">
              <div className="home-pricing-header">
                <h3 className="h5 fw-bold mb-0">Corporate</h3>
                <span className="home-badge">Sob consulta</span>
              </div>
              <div className="home-price">Sob consulta</div>
              <p className="muted small">Para grupos e operações multiunidade.</p>
              <ul className="home-list-unstyled home-pricing-list">
                <li>Multiunidade avançado</li><li>Integrações e relatórios avançados</li><li>SLA e governança ampliada</li><li>Onboarding e acompanhamento</li>
              </ul>
              <button type="button" className="btn btn-outline w-100" onClick={() => setContactModalOpen(true)}>Falar com comercial</button>
            </div>
          </div>
          <p className="home-small-note mt-3 reveal">* Licenças incluem LP/LI/LO, Outorga, Autorizações e documentos com condicionantes e prazos.</p>
        </div>
      </section>

      <section id="trial" className="home-section">
        <div className="home-container">
          <div className="home-trial-grid">
            <div className="reveal">
              <h2 className="home-section-title">Teste grátis por 7 dias (controlado)</h2>
              <p className="text-secondary mb-4">
                Sem cartão. Para ativar o teste, precisamos de <strong>1 PDF de licença</strong>. Você verá o “processo completo”: condicionantes virando tarefas com prazos e evidências.
              </p>
              <div className="home-card p-4">
                <div className="fw-bold mb-2">O que entra no teste</div>
                <ul className="mb-0 text-secondary">
                  <li>1 empreendimento</li><li>1 licença ativa</li><li>Até 2 usuários</li><li>Até 10 arquivos</li><li>1 extração de condicionantes com IA</li>
                </ul>
              </div>
            </div>
            <div className="home-card reveal">
              <div className="home-card-header mb-3">
                <div><div className="fw-bold">Solicitar ativação do teste</div><div className="home-small-note">Nós retornamos por WhatsApp/e-mail.</div></div>
                <span className="home-badge home-badge-success">7 dias</span>
              </div>
              <form ref={trialFormRef} onSubmit={handleTrialSubmit} className="home-trial-form">
                <div className="form-group">
                  <label className="form-label">Nome</label>
                  <input type="text" className="form-control" name="nome" placeholder="Seu nome" required />
                </div>
                <div className="form-group">
                  <label className="form-label">WhatsApp</label>
                  <input
                    type="tel"
                    className="form-control"
                    name="whatsapp"
                    placeholder="(67) 99999-9999"
                    required
                    onChange={(e) => {
                      e.target.value = formatPhone(e.target.value);
                    }}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Empresa</label>
                  <input type="text" className="form-control" name="empresa" placeholder="Nome da empresa" required />
                </div>
                <div className="home-trial-form-actions">
                  <button type="submit" className="btn btn-primary">Quero testar 7 dias</button>
                  <button type="button" className="btn btn-outline" onClick={copyTrialMessage}>
                    {copyFeedback === 'trial' ? 'Mensagem copiada ✅' : 'Copiar mensagem para WhatsApp'}
                  </button>
                </div>
                <p className="home-small-note mt-3">Ao solicitar o teste, você concorda em receber contato para ativação e orientação inicial.</p>
              </form>
            </div>
          </div>
        </div>
      </section>

      <section id="faq" className="home-section">
        <div className="home-container">
          <div className="home-section-header reveal">
            <h2 className="home-section-title">Perguntas frequentes</h2>
            <p className="home-section-lead">Respostas diretas para acelerar sua decisão.</p>
          </div>
          <div className="home-faq reveal">
            {faqItems.map((item) => (
              <div key={item.id} className="home-card home-faq-item">
                <button type="button" className="home-faq-question" onClick={() => setFaqOpen(faqOpen === item.id ? null : item.id)}>
                  {item.q}
                  <span className="home-faq-icon">{faqOpen === item.id ? '−' : '+'}</span>
                </button>
                {faqOpen === item.id && <div className="home-faq-answer">{item.a}</div>}
              </div>
            ))}
          </div>
          <div className="text-center mt-4 reveal">
            <button type="button" className="btn btn-primary" onClick={() => scrollToId('trial')}>Testar 7 dias</button>
          </div>
        </div>
      </section>

      <section id="seguranca" className="home-section">
        <div className="home-container">
          <div className="home-section-header reveal">
            <h2 className="home-section-title">Segurança e conformidade</h2>
            <p className="home-section-lead">Requisitos obrigatórios para indústria: criptografia, backups, controle de acesso, logs de auditoria e conformidade com LGPD.</p>
          </div>
          <div className="home-beneficios-grid">
            {[
              { icon: '🔐', title: 'Criptografia / Backups', desc: 'Dados criptografados e backups automáticos.' },
              { icon: '👥', title: 'Controle de acesso', desc: 'Permissões por usuário e função.' },
              { icon: '📋', title: 'Logs de auditoria', desc: 'Rastreabilidade completa de ações.' },
              { icon: '🛡️', title: 'LGPD', desc: 'Conformidade com dados e retenção.' }
            ].map((b, i) => (
              <div key={i} className="home-card reveal">
                <div className="home-beneficio">
                  <div className="home-feature-icon">{b.icon}</div>
                  <div><div className="fw-bold">{b.title}</div><div className="muted">{b.desc}</div></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="home-footer">
        <div className="home-container">
          <div className="home-footer-inner">
            <div>
              <div className="fw-bold">Green Check</div>
              <div className="home-small-note">Gestão de Licenças, Condicionantes e Evidências com IA.</div>
            </div>
            <div className="home-footer-links">
              <div className="home-small-note">© {new Date().getFullYear()} Green Check</div>
              <button type="button" className="home-footer-link" onClick={() => setContactModalOpen(true)}>Contato</button>
            </div>
          </div>
        </div>
      </footer>

      {demoModalOpen && (
        <div className="home-modal-overlay" onClick={() => setDemoModalOpen(false)}>
          <div className="home-modal glass" onClick={(e) => e.stopPropagation()}>
            <div className="home-modal-header">
              <h3 className="home-modal-title">Demo rápida (2 min)</h3>
              <button type="button" className="home-modal-close" onClick={() => setDemoModalOpen(false)} aria-label="Fechar"><FiX /></button>
            </div>
            <div className="home-modal-body">
              <p className="text-secondary">Troque este vídeo por um embed do YouTube/Vimeo quando tiver.</p>
              <div className="home-demo-placeholder glass">
                <div className="home-demo-placeholder-inner">
                  <div className="fw-bold">Seu vídeo aqui</div>
                  <div className="muted">Ex.: “PDF da licença → tarefas + evidências”</div>
                </div>
              </div>
            </div>
            <div className="home-modal-footer">
              <button type="button" className="btn btn-primary" onClick={() => { setDemoModalOpen(false); scrollToId('trial'); }}>Testar 7 dias</button>
              <button type="button" className="btn btn-outline" onClick={() => setDemoModalOpen(false)}>Fechar</button>
            </div>
          </div>
        </div>
      )}

      {contactModalOpen && (
        <div className="home-modal-overlay" onClick={() => setContactModalOpen(false)}>
          <div className="home-modal glass" onClick={(e) => e.stopPropagation()}>
            <div className="home-modal-header">
              <h3 className="home-modal-title">Falar com o comercial</h3>
              <button type="button" className="home-modal-close" onClick={() => setContactModalOpen(false)} aria-label="Fechar"><FiX /></button>
            </div>
            <div className="home-modal-body">
              <p className="text-secondary mb-3">Escolha um canal:</p>
              <div className="home-modal-buttons">
                <button type="button" className="btn btn-primary" onClick={openWhatsContact}>WhatsApp (mensagem pronta)</button>
                <button type="button" className="btn btn-outline" onClick={copyEmailContact}>
                  {copyFeedback === 'email' ? 'Texto copiado ✅' : 'Copiar texto para e-mail'}
                </button>
              </div>
              <p className="home-small-note mt-3">* Edite os contatos no código (CONTACTS) em Home.js.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
